// src/enums/reservation.ts
// A Reservation is created when a customer submits an "Enquire / Reserve"
// request from the client site for a specific car. No payment gateway is
// used — deposits are recorded manually (cash / bank transfer) once received
// at the branch. This is the core of the Sales module for Phase 2.
import { Timestamp } from 'firebase/firestore';

export const RESERVATION_STATUSES = [
  'New Inquiry', 'Contacted', 'Approved', 'Rejected', 'Test Drive Scheduled', 'Reserved (Deposit Paid)',
  'Sold', 'Cancelled', 'Lost',
] as const;
export type ReservationStatus = typeof RESERVATION_STATUSES[number];

export const PAYMENT_METHODS = [
  'Cash', 'Bank Transfer', 'Cheque', 'Financing', 'Trade-in Credit', 'Not Paid Yet',
] as const;
export type PaymentMethodType = typeof PAYMENT_METHODS[number];

export const DELIVERY_METHODS = ['Pickup at Branch', 'Dealer Delivery'] as const;
export type DeliveryMethod = typeof DELIVERY_METHODS[number];

// Vehicle-appropriate cancellation reasons — replaces the old e-commerce
// ReturnReason enum ('size_issue', 'wrong_item', etc. — see answers.text).
export const CANCELLATION_REASONS = [
  'Financing Fell Through', 'Customer Changed Mind', 'Failed Inspection',
  'Dealer Cancelled', 'Other',
] as const;
export type CancellationReasonType = typeof CANCELLATION_REASONS[number];

export const INQUIRY_TYPES = ['Purchase / Reservation', 'Test Drive Request', 'General Inquiry'] as const;
export type InquiryType = typeof INQUIRY_TYPES[number];

export interface Reservation {
  id?: string;
  inquiryType: InquiryType;
  carId: string;
  carName?: string;
  carImage?: string;
  carPrice?: number;
  // Set instead of carId when the customer picked a brand/model without
  // narrowing down to one specific vehicle (e.g. a general test-drive request
  // for "a Toyota Corolla", not tied to one VIN).
  brandId?: string;
  brandName?: string;
  modelId?: string;
  modelName?: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  preferredBranchId?: string;
  preferredBranchName?: string;
  preferredDate?: string; // for test drive requests
  preferredTime?: string; // for test drive requests
  message?: string;
  status: ReservationStatus;
  depositAmount?: number;
  paymentMethod?: PaymentMethodType;
  deliveryMethod?: DeliveryMethod;
  cancellationReason?: CancellationReasonType;
  // Free-text reason staff can attach when approving/rejecting a reservation
  // or test drive request — included in the customer notification email
  // when set (see functions/src/email.ts sendReservationDecisionEmail).
  decisionReason?: string;
  assignedToUserId?: string;
  assignedToName?: string;
  internalNotes?: string;
  saleFinalizedAt?: Date | Timestamp | null;
  finalSalePrice?: number;
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

export interface ReservationFormData {
  status: string;
  depositAmount: string;
  paymentMethod: string;
  deliveryMethod: string;
  cancellationReason: string;
  assignedToUserId: string;
  internalNotes: string;
  finalSalePrice: string;
}
